class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.onOverlayClick);
    this.setHeaderCartIconAccessibility();

    // Human Matter: the header section can be re-rendered (Theme Editor) — a new cart icon must still open the drawer,
    // so the click/space handlers live on the document and the attributes are re-applied on section load
    document.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!link) return;
      event.preventDefault();
      this.open(link);
    });
    document.addEventListener('keydown', (event) => {
      const link = event.target.closest && event.target.closest('#cart-icon-bubble');
      if (!link || event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(link);
    });
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target && event.target.querySelector && event.target.querySelector('#cart-icon-bubble')) this.setHeaderCartIconAccessibility();
    });

    // Human Matter: Dawn leaves a custom validity message on a quantity field after restoring its valid number
    // (0 typed, minimum 1); the checkout button then fails native validation. A field that shows the cart's own
    // quantity is valid: clear the stale message before the checkout button submits.
    this.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest || !event.target.closest('[name="checkout"]')) return;
        this.querySelectorAll('.quantity__input').forEach((input) => {
          if (input.value === input.getAttribute('value')) input.setCustomValidity('');
        });
      },
      true
    );

    // Human Matter: when a removal empties the cart, Dawn traps focus inside .drawer__inner-empty;
    // here the close button and "Continue shopping" sit outside it, so widen the trap to the dialog.
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'cart-items' || !this.classList.contains('active')) return;
      if (!event.cartData || event.cartData.item_count !== 0) return;
      const container = document.getElementById('CartDrawer') || this;
      const active = document.activeElement;
      trapFocus(container, active && container.contains(active) ? active : this.querySelector('.drawer__close'));
    });

    // Human Matter: the "You might like" tiles carry their own <select name="id"> inside <cart-drawer-items>;
    // Dawn's CartItems treats every `change` as a quantity field. Keep the tiles' changes to themselves.
    this.addEventListener(
      'change',
      (event) => {
        if (event.target.closest && event.target.closest('.e-rec')) event.stopPropagation();
      },
      true
    );

    // Human Matter: while the drawer fetches its sections a second time (preview without `_fd=0`),
    // a second click on an add-to-cart form would add the item again. Hold it until the drawer is drawn.
    this.retrying = false;
    document.addEventListener(
      'submit',
      (event) => {
        if (!this.retrying) return;
        const form = event.target;
        if (form && form.matches && form.matches('form[action*="/cart/add"]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    // Noom (T02, 13.09; v4 after review R1/R2): ONE operation at a time. `unconfirmed` (an add the store did not confirm)
    // and `needsSync` (a quantity change it did not confirm) survive until a READ of the real cart resolves them. While a
    // read is in flight (`reading`) or the drawer re-fetches its sections (`retrying`), further add submits are ignored —
    // from any button. A submit that arrives while the cart is uncertain is captured as an intent (variant, quantity,
    // form) and carried out once AFTER the read: on its own form if still in the document, else on the current form for
    // that variant (recommendation tiles come back with the re-rendered drawer), else the shopper is told to tap again.
    // No mutation is ever repeated on its own.
    this.unconfirmed = null;
    this.pendingAdd = null;
    this.needsSync = false;
    this.reading = false;
    this.readSeq = 0;
    this.crossTabDirty = false;
    this.crossTabRevision = 0;
    this.backgrounded = false;
    this.crossTabListenersInstalled = false;
    this.onCrossTabBlur = () => { this.backgrounded = true; };
    this.onCrossTabFocus = () => {
      if (this.backgrounded) {
        this.backgrounded = false;
        this.markCrossTabDirty();
      }
    };
    this.onCrossTabVisibility = () => {
      if (document.hidden) this.backgrounded = true;
      else if (this.backgrounded) {
        this.backgrounded = false;
        this.markCrossTabDirty();
      }
    };
    this.onCrossTabStorage = (event) => {
      if (event.key === 'noom-cart-revision') this.markCrossTabDirty();
    };
    this.onStaleLineClick = (event) => {
      if (!this.crossTabDirty || !event.target.closest?.('cart-drawer-items .quantity__button, cart-drawer-items cart-remove-button')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.refreshIfStale(); // never replay the click against a changed line
    };
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (event.defaultPrevented || !form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
        const quantityBusy = typeof CartItems !== 'undefined' && (CartItems.activeUpdate || CartItems.draining || CartItems.pendingChanges?.size);
        if (this.retrying || this.reading || this.pendingAdd || this.headerAddActive || this.classList.contains('is-adding') || quantityBusy || window.HMLpBonus?.active) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        // A confirmed paid add whose gift failed is resumed by the same form without another paid write.
        if (form.closest('product-form[data-direct-checkout="true"]')?.getCheckoutRecovery?.()) return;
        const intent = this.captureIntent(form, event.submitter || form.querySelector('[type="submit"]'));
        if (this.unconfirmed || this.needsSync) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.verifyThenResubmit(intent);
          return;
        }
        this.pendingAdd = { variantId: intent.variantId, quantity: intent.quantity, identity: intent.identity, qtyBefore: this.quantityOf(intent.variantId, intent.identity) };
      },
      true
    );
    this.addEventListener('click', (event) => {
      if (!event.target.closest) return;
      // Noom LP (Kamil 14.09): a page whose product section carries data-hm-cart-focus (a setting of that section, so only that
      // landing) keeps the customer on the landing — links to products do nothing, "continue shopping" just closes the drawer.
      // Read from the DOM on every click: survives UTM, drawer re-renders and the editor; nothing stored in cookies or the cart.
      if (document.querySelector('[data-hm-cart-focus]')) {
        if (event.target.closest('.e-cart-continue, .e-cart-continue-pill')) {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.target.closest('.cart-item__name, .cart-item__link, .e-rec__name, .e-rec__media')) {
          event.preventDefault();
          return;
        }
      }
      if (event.target.closest('[data-cart-error-retry]')) {
        event.preventDefault();
        this.retryRead();
      } else if (event.target.closest('[data-cart-error-close]')) {
        event.preventDefault();
        this.close();
      }
    });

    // Noom LP (Kamil 12.09): the drawer opens the moment an add-to-cart form is submitted — not after the request
    // answers and the fly-to-cart flight lands. `is-adding` shows a placeholder card and dims the current contents
    // (elora-cart.css); renderContents() clears it, a refused add (cartError) closes the drawer again so the form's own
    // error message is visible, and a request that never answers gives the drawer back after 12 s.
    if (this.hasAttribute('data-instant-open')) {
      this.openedAt = 0;
      this.addingTimer = null;
      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (event.defaultPrevented || !form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
          if (form.closest('product-form[data-direct-checkout="true"]')) return;
          if (this.retrying) return;
          this.setAdding(true);
          this.open(form.querySelector('[type="submit"]') || undefined);
        },
        true
      );
    }

    // Noom (T02, 13.09): a refused add (Shopify's JSON 422 …) closes the drawer so the form's own message is visible;
    // an unconfirmed one (product-form.js: HTML/429, timeout, network) keeps it open with the error card and arms the
    // read-before-next-add check above. Existing lines stay untouched either way.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartError) {
      subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        if (event && event.kind === 'unconfirmed') {
          const variantId = event.productVariantId;
          this.unconfirmed = this.pendingAdd || { variantId, quantity: event.quantity || 1, qtyBefore: this.quantityOf(variantId) };
          this.pendingAdd = null;
          this.setNeedsSync(true);
          if (event.directCheckout) { this.setAdding(false); return; }
          this.showError(event.reason === 'ratelimit' ? 'ratelimit' : event.reason === 'network' ? 'network' : 'unconfirmed');
          return;
        }
        this.pendingAdd = null;
        if (!this.classList.contains('is-adding')) return;
        this.setAdding(false);
        this.close();
      });
    }
  }

  setAdding(on) {
    window.clearTimeout(this.addingTimer);
    this.classList.toggle('is-adding', !!on);
    if (on)
      this.addingTimer = window.setTimeout(() => {
        // nothing answered for 20 s (the form's own 12 s timeout should have spoken first): the add is unconfirmed
        if (!this.classList.contains('is-adding')) return;
        if (this.pendingAdd) {
          this.unconfirmed = this.pendingAdd;
          this.pendingAdd = null;
        }
        this.setNeedsSync(true);
        this.showError('unconfirmed');
      }, 20000);
  }

  // Noom (T02, 13.09) — error card, read-only recovery ---------------------------------------------------------------
  // how many of this variant the drawer currently shows (the drawer is re-rendered after every change)
  quantityOf(variantId, identity) {
    if (!variantId) return 0;
    let n = 0;
    this.querySelectorAll('input.quantity__input[data-quantity-variant-id]').forEach((input) => {
      if (String(input.dataset.quantityVariantId) !== String(variantId)) return;
      if (identity && window.HMLpBonusUI?.inputIdentity(input) !== identity) return;
      n += parseInt(input.value, 10) || 0;
    });
    return n;
  }

  errorCard() {
    return this.querySelector('.e-cart-error');
  }

  // wording of the error card (snippets/hm-cart-drawer.liquid, `data-msg-*` from the locale); Dawn's line is the fallback
  errorText(kind) {
    const card = this.errorCard();
    const key = 'msg' + String(kind || '').charAt(0).toUpperCase() + String(kind || '').slice(1);
    const text = card && card.dataset ? card.dataset[key] || card.dataset.msgUnconfirmed : '';
    return text || (window.cartStrings && window.cartStrings.error) || '';
  }

  // kinds: unconfirmed | ratelimit | network | change | render | refreshfailed | already
  showError(kind) {
    this.setAdding(false);
    const card = this.errorCard();
    if (!card) {
      const errors = this.querySelector('#CartDrawer-CartErrors');
      if (errors) errors.textContent = (window.cartStrings && window.cartStrings.error) || '';
      return;
    }
    const line = card.querySelector('.e-cart-error__t');
    if (line) line.textContent = this.errorText(kind);
    const link = card.querySelector('[data-cart-error-link]');
    if (link) link.hidden = kind !== 'render';
    const retry = card.querySelector('[data-cart-error-retry]');
    if (retry) {
      retry.hidden = kind === 'already' || kind === 'retrylost' || kind === 'linegone';
      retry.removeAttribute('aria-busy');
    }
    card.hidden = false;
    this.classList.add('has-error');
    this.containTab(true);
    if (!this.classList.contains('active')) this.open();
    window.setTimeout(() => {
      if (!this.classList.contains('has-error') || !this.classList.contains('active')) return;
      const container = document.getElementById('CartDrawer') || this;
      const target = retry && !retry.hidden ? retry : card.querySelector('[data-cart-error-close]');
      if (target && (container.contains(document.activeElement) || document.activeElement === document.body)) trapFocus(container, target);
    }, 400);
  }

  clearError() {
    const card = this.errorCard();
    if (card) {
      card.hidden = true;
      const line = card.querySelector('.e-cart-error__t');
      if (line) line.textContent = '';
    }
    this.containTab(false);
    this.classList.remove('has-error');
  }

  // a read that does not answer within this time ends in a readable state (card), never in a silent wait (review R4)
  static get READ_TIMEOUT_MS() {
    return 12000;
  }

  // GET the drawer's sections for the page we are on (Section Rendering API) — a read, never a mutation.
  // Resolves with usable sections; rejects with { reason: 'ratelimit' | 'network' | 'timeout' | 'refreshfailed' }.
  readCart() {
    // Bonus recovery reads the actual cart first. It can only repair free lines;
    // the existing paid-product uncertainty is resolved by the caller afterwards.
    if (window.HMLpBonusUI?.needsRecovery()) return window.HMLpBonusUI.recover(this);
    const ids = this.getSectionsToRender().map((section) => section.id).join(',');
    const url = window.location.pathname + window.location.search + (window.location.search ? '&' : '?') + 'sections=' + ids;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), CartDrawer.READ_TIMEOUT_MS) : null;
    // no Accept header: with it the storefront answers the page's own JSON ({"page": …}) instead of the sections (13.09 debug)
    return fetch(url, { cache: 'no-store', signal: controller ? controller.signal : undefined })
      .then(
        (response) =>
          response.text().then((text) => {
            let sections = null;
            try {
              sections = JSON.parse(text);
            } catch (e) {
              sections = null;
            }
            if (!this.sectionsComplete(sections)) {
              throw Object.assign(new Error('cart sections unreadable ' + response.status), { reason: response.status === 429 ? 'ratelimit' : 'refreshfailed' });
            }
            return sections;
          }),
        (e) => {
          const reason = e && e.name === 'AbortError' ? 'timeout' : 'network';
          throw Object.assign(new Error('cart sections: ' + reason), { reason });
        }
      )
      .finally(() => {
        if (timer) window.clearTimeout(timer);
      });
  }

  // Noom (T02 v5, review C1): ONE read manager for every read of the cart — verify, sync, manual refresh and the
  // fallback fetch of missing sections. Each read takes a sequence number; only the NEWEST read may render, report or
  // release the locks, so a late answer of an older (hung or superseded) read can never overwrite a fresher view.
  startRead(kind) {
    const seq = ++this.readSeq;
    this.reading = true;
    this.retrying = kind === 'fallback';
    return seq;
  }

  isCurrentRead(seq) {
    return seq === this.readSeq;
  }

  // true (and locks released) only for the newest read; false = stale answer, ignore it
  endRead(seq) {
    if (!this.isCurrentRead(seq)) return false;
    this.reading = false;
    this.retrying = false;
    return true;
  }

  readErrorKind(e) {
    return e && (e.reason === 'network' || e.reason === 'ratelimit') ? e.reason : 'refreshfailed';
  }

  // the cart is uncertain: the lines wait (CSS `needs-sync`) for a read of the real cart before any further change
  setNeedsSync(on) {
    this.needsSync = !!on;
    this.classList.toggle('needs-sync', !!on);
  }

  connectedCallback() {
    if (this.crossTabListenersInstalled) return;
    this.crossTabListenersInstalled = true;
    // A line key can change in another tab after a discount. The page and
    // drawer refresh once on return or on a confirmed sibling-tab write.
    window.addEventListener('blur', this.onCrossTabBlur);
    window.addEventListener('focus', this.onCrossTabFocus);
    document.addEventListener('visibilitychange', this.onCrossTabVisibility);
    window.addEventListener('storage', this.onCrossTabStorage);
    this.addEventListener('click', this.onStaleLineClick, true);
  }

  markCrossTabDirty() {
    this.crossTabDirty = true;
    this.crossTabRevision += 1;
    this.refreshIfStale();
  }

  refreshIfStale() {
    const cartPageVisible = !!document.querySelector('cart-items') && !!document.getElementById('main-cart-items');
    if (!this.crossTabDirty || this.backgrounded || document.hidden ||
        (!this.classList.contains('active') && !cartPageVisible) || this.reading || this.retrying ||
        this.classList.contains('is-adding') || window.HMLpBonus?.active) return;
    this.retryRead();
  }

  // what a submit asked for, independent of the form element's lifetime (the drawer's tiles are re-rendered with it)
  captureIntent(form, submitter) {
    const idField = form.querySelector('[name="id"]');
    const qtyField = form.querySelector('[name="quantity"]');
    const data = window.HMLpBonus ? new FormData(form) : null;
    if (data && form.hmRetryData) {
      for (const key of new Set(Array.from(data.keys()))) data.delete(key);
      form.hmRetryData.forEach(([key, value]) => data.append(key, value));
    }
    return {
      form,
      submitter: submitter || null,
      variantId: data ? String(data.get('id') || '') : idField ? String(idField.value) : '',
      quantity: data ? parseInt(data.get('quantity'), 10) || 1 : qtyField ? parseInt(qtyField.value, 10) || 1 : 1,
      inDrawer: this.contains(form),
      identity: data ? window.HMLpBonus.identity(data) : null,
      entries: data ? Array.from(data.entries()) : null,
    };
  }

  // carry a captured click out once: its own form when still in the document, else the current form for that variant
  // (a re-rendered recommendation tile), else say so — the click is never dropped silently (review R2)
  resubmit(intent) {
    let form = intent.form && intent.form.isConnected ? intent.form : null;
    if (!form) {
      form =
        Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find((candidate) => {
          const idField = candidate.querySelector('[name="id"]');
          return !!idField && String(idField.value) === String(intent.variantId) && (!intent.inDrawer || this.contains(candidate)) &&
            (!intent.identity || window.HMLpBonus?.identity(new FormData(candidate)) === intent.identity);
        }) || null;
    }
    if (!form) {
      this.showError('retrylost');
      return;
    }
    const submitter =
      intent.submitter && intent.submitter.isConnected && form.contains(intent.submitter) ? intent.submitter : form.querySelector('[type="submit"]');
    if (intent.entries) form.hmRetryData = intent.entries;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
    else if (submitter) submitter.click();
  }

  // A quantity change while the cart is uncertain reads the real cart first.
  // Discount changes can replace a line key, so use the unique product identity
  // after the read if the former key is gone. Never use the old line position.
  syncThenChange(intent) {
    if (this.reading) return Promise.resolve(false);
    const seq = this.startRead('sync');
    this.clearError();
    this.setAdding(true);
    if (!this.classList.contains('active')) this.open();
    return this.readCart()
      .then((sections) => {
        if (!this.endRead(seq)) return false; // a newer read has taken over: this answer is stale (sync)
        this.renderContents({ sections, sectionsRetried: true });
        let input = intent.lineKey
          ? Array.from(this.querySelectorAll('input.quantity__input[data-quantity-line-key]')).find((el) => el.dataset.quantityLineKey === intent.lineKey)
          : null;
        if (!input && intent.identity && window.HMLpBonusUI) {
          const matches = Array.from(this.querySelectorAll('input.quantity__input[data-quantity-line-key]'))
            .filter((el) => window.HMLpBonusUI.inputIdentity(el) === intent.identity);
          if (matches.length === 1) input = matches[0];
        }
        const items = this.querySelector('cart-drawer-items');
        if (!input || !items || typeof items.updateQuantity !== 'function') {
          this.showError('linegone');
          return false;
        }
        if (parseInt(input.value, 10) === intent.quantity) return true; // the cart already holds what was asked for
        return items.updateQuantity(input.dataset.index, intent.quantity, { currentTarget: null }, intent.name, input.dataset.quantityVariantId);
      })
      .catch((e) => {
        if (!this.endRead(seq)) return false;
        this.setNeedsSync(true);
        this.showError(this.readErrorKind(e));
        return false;
      });
  }

  // the card's "Refresh cart": show the real cart; the next add then needs no verification. A deliberate refresh
  // supersedes any read still hanging (its late answer is dropped by the sequence check).
  retryRead() {
    const card = this.errorCard();
    const retry = card && card.querySelector('[data-cart-error-retry]');
    if (retry) {
      if (retry.getAttribute('aria-busy') === 'true') return;
      retry.setAttribute('aria-busy', 'true');
    }
    const seq = this.startRead('refresh');
    const revision = this.crossTabRevision;
    const keepClosed = !this.classList.contains('active') && !!document.querySelector('cart-items');
    this.readCart()
      .then((sections) => {
        if (!this.endRead(seq)) return;
        this.clearError();
        this.renderContents({ sections, sectionsRetried: true }, { keepClosed });
        if (this.crossTabRevision === revision) this.crossTabDirty = false;
        else this.refreshIfStale();
      })
      .catch((e) => {
        if (!this.endRead(seq)) return;
        this.showError(this.readErrorKind(e));
      });
  }

  // an add is asked for while the cart is uncertain: read first, then either say "already in the cart" or carry this one
  // click out once (normal submit path). The uncertainty survives a failed read; extra clicks during the read are ignored.
  verifyThenResubmit(intent) {
    if (this.reading) return;
    const pending = this.unconfirmed; // kept until the read resolves (review R1)
    const seq = this.startRead('verify');
    this.clearError();
    // the form still shows the message from the earlier attempt: this click starts a new one (product-form does the same on submit)
    const productForm = intent.form.closest('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
    this.setAdding(true);
    this.open(intent.submitter || undefined);
    this.readCart()
      .then((sections) => {
        if (!this.endRead(seq)) return; // a newer read has taken over: this answer is stale (verify)
        this.renderContents({ sections, sectionsRetried: true }); // the real state: clears unconfirmed / needsSync
        const landed =
          !!pending &&
          String(pending.variantId) === String(intent.variantId) &&
          (!pending.identity || pending.identity === intent.identity) &&
          this.quantityOf(intent.variantId, intent.identity) >= (pending.qtyBefore || 0) + (pending.quantity || 1);
        if (landed) {
          this.showError('already');
          return;
        }
        this.resubmit(intent);
      })
      .catch((e) => {
        if (!this.endRead(seq)) return;
        this.unconfirmed = pending;
        this.setNeedsSync(true);
        this.showError(this.readErrorKind(e));
      });
  }

  // Dawn's trapFocus wraps only at the first/last focusable element in DOM order; in the error state (and with an empty
  // cart) those sit in hidden parts of the drawer, so Tab from the last VISIBLE control would leave the dialog. While the
  // card is shown, Tab and Shift+Tab stay within the visible controls.
  containTab(on) {
    if (!this.onTabKeydown) {
      this.onTabKeydown = (event) => {
        if (event.key !== 'Tab' || !this.classList.contains('has-error') || !this.classList.contains('active')) return;
        const container = document.getElementById('CartDrawer') || this;
        const visible = getFocusableElements(container).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0);
        if (!visible.length) return;
        const first = visible[0];
        const last = visible[visible.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !container.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !container.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      };
    }
    document.removeEventListener('keydown', this.onTabKeydown, true);
    if (on) document.addEventListener('keydown', this.onTabKeydown, true);
  }

  // the rendered section says whether the cart is empty (after a read it may well be)
  renderedEmpty(sections) {
    const html = sections && sections['hm-cart-drawer'];
    if (typeof html !== 'string') return false;
    const el = this.getSectionDOM(html, 'cart-drawer');
    return !!el && el.classList.contains('is-empty');
  }

  onOverlayClick() {
    // a fast second click of a double click lands on the overlay that has just appeared: keep the drawer open
    if (Date.now() - (this.openedAt || 0) < 450) return;
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.openedAt = Date.now();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.refreshIfStale();
    });

    this.addEventListener('transitionend', () => this.trapDialogFocus(), { once: true });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.(); // Noom T05: no-op without Standard Events (see hm-cart.js)
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.crossTabListenersInstalled) {
      this.crossTabListenersInstalled = false;
      window.removeEventListener('blur', this.onCrossTabBlur);
      window.removeEventListener('focus', this.onCrossTabFocus);
      document.removeEventListener('visibilitychange', this.onCrossTabVisibility);
      window.removeEventListener('storage', this.onCrossTabStorage);
      this.removeEventListener('click', this.onStaleLineClick, true);
    }
  }

  trapDialogFocus() {
    // Human Matter: the whole dialog, also when the cart is empty — the close button and
    // "Continue shopping" sit outside .drawer__inner-empty, so Tab must reach them.
    const container = document.getElementById('CartDrawer') || this;
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    trapFocus(container, focusElement);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  sectionsComplete(sections) {
    // Section Rendering may answer HTTP 200 with a section set to null; render only when every
    // section is real HTML that contains its target element.
    if (!sections) return false;
    return this.getSectionsToRender().every((section) => {
      const html = sections[section.id];
      return typeof html === 'string' && !!this.getSectionDOM(html, section.selector || '.shopify-section');
    });
  }

  // Noom (T06, 17.09): an add response carries the freshly rendered drawer section, whose
  // server-side receipt is the complete cart after the mutation. Use it for standard events
  // on every product; never read the currently mounted drawer because it may still be stale.
  cartFromResponse(response) {
    if (!response || typeof response !== 'object' || Array.isArray(response)) return null;

    let cart = this.validCartReceipt(response) ? response : null;
    if (!cart) {
      const checkoutReceipt = response.sections && response.sections['hm-checkout-state'];
      const html = typeof checkoutReceipt === 'string' ? checkoutReceipt : response.sections && response.sections['hm-cart-drawer'];
      if (typeof html !== 'string') return null;
      try {
        const receipt = this.getSectionDOM(html, typeof checkoutReceipt === 'string'
          ? 'script[data-hm-checkout-state]' : '#CartDrawer script[data-hm-cart-state]');
        cart = JSON.parse(receipt?.textContent || 'null');
      } catch (_) {
        return null;
      }
    }

    if (!this.validCartReceipt(cart)) return null;
    return { ...cart, sections: response.sections };
  }

  validCartReceipt(cart) {
    if (!cart || typeof cart !== 'object' || Array.isArray(cart)) return false;
    if (typeof cart.currency !== 'string' || !/^[A-Z]{3}$/.test(cart.currency)) return false;
    const totals = ['item_count', 'items_subtotal_price', 'original_total_price', 'total_price', 'total_discount', 'total_weight'];
    if (!totals.every((field) => typeof cart[field] === 'number' && Number.isFinite(cart[field]) && cart[field] >= 0)) return false;
    if (!Array.isArray(cart.items)) return false;
    const keys = new Set();
    return cart.items.every((item) => item && typeof item === 'object' && !Array.isArray(item) &&
      typeof item.key === 'string' && item.key.trim().length > 0 && !keys.has(item.key) && keys.add(item.key) &&
      Number.isInteger(item.variant_id) && item.variant_id > 0 && Number.isInteger(item.quantity) && item.quantity > 0);
  }

  // Noom (Kamil 14.09, old product pages): the Elixir sticky bar calls the OLD drawer's cartDrawer.addItemToCart(variantId)
  // (sections/sticky-add-to-cart.liquid). The add goes through the page's own product form, so the one add path of
  // hm-product-form.js (verification while uncertain, response sections, opening, analytics events) is used — no second
  // cart mechanism. Returns false when the page has no product form for that variant.
  addItemToCart(variantId, quantity) {
    const id = variantId == null ? '' : String(variantId);
    const idOf = (form) => form.querySelector('[name="id"]') || (form.id ? document.querySelector(`[name="id"][form="${form.id}"]`) : null);
    const forms = Array.from(document.querySelectorAll('product-form form[action*="/cart/add"]')).filter((f) => !f.closest('cart-drawer'));
    const form = forms.find((f) => { const i = idOf(f); return i && id && String(i.value) === id; }) || forms.find((f) => idOf(f)) || null;
    if (!form) return false;
    const idInput = idOf(form);
    if (idInput && id && String(idInput.value) !== id && idInput.tagName === 'INPUT') idInput.value = id;
    const qty = form.querySelector('[name="quantity"]') || (form.id ? document.querySelector(`[name="quantity"][form="${form.id}"]`) : null);
    if (qty && quantity) qty.value = String(quantity);
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    return true;
  }

  goToCartPage() {
    window.location = (window.routes && window.routes.cart_url) || '/cart';
  }

  renderContents(parsedState, options = {}) {
    if (!parsedState) {
      this.goToCartPage();
      return false;
    }
    if (!this.sectionsComplete(parsedState.sections)) {
      // Section Rendering can come back without usable `sections` (e.g. the storefront domain is not connected
      // yet and the request went out without `_fd=0`). Fetch the sections from the page we are on and render
      // the drawer here; only if that fails too does the shopper go to the cart page.
      if (parsedState.sectionsRetried) {
        // Noom (T02, 13.09): the item is in the cart but the drawer cannot be drawn — say so and offer the cart page,
        // instead of sending the shopper away by itself
        this.showError('render');
        return false;
      }
      // Noom (T02 v5, review C1): the fallback read goes through the same read manager and readCart() (12 s limit):
      // a hung or superseded fallback releases the locks by itself and its late answer is dropped
      const seq = this.startRead('fallback');
      return this.readCart()
        .then((sections) => {
          if (!this.endRead(seq)) return false;
          parsedState.sections = sections;
          parsedState.sectionsRetried = true;
          return this.renderContents(parsedState, options);
        })
        .catch(() => {
          if (!this.endRead(seq)) return false;
          // the item IS in the cart (the add answered 200) but the drawer could not be drawn: the lines on screen are
          // stale until a read, so changes wait for one; no automatic redirect to the cart page
          this.setNeedsSync(true);
          this.showError('render');
          return false;
        });
    }
    this.classList.remove('is-empty');
    // Noom (T02, 13.09): the section knows whether the cart is empty; a fresh render is the real state, so the error card
    // and the read-before-next-add guard are cleared
    if (this.renderedEmpty(parsedState.sections)) this.classList.add('is-empty');
    this.setAdding(false);
    this.clearError();
    this.unconfirmed = null;
    this.pendingAdd = null;
    this.setNeedsSync(false);
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.target
        ? document.querySelector(section.target)
        : section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

      if (!sectionElement) return;
      // Noom T05 (13.09): on the old Elixir layout (theme.liquid) the header keeps its own cart icon; the drawer is rendered
      // with data-bubble-mode="count", so only the count bubble inside #cart-icon-bubble is synced from the fetched section
      if (section.target === '#cart-icon-bubble' && this.dataset.bubbleMode === 'count') {
        this.syncCountBubble(sectionElement, parsedState.sections[section.id]);
        return;
      }
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.onOverlayClick);
      // an add from inside the open drawer (quick add) replaces its content: the focus trap
      // and the focused element are gone with it, so set them again
      if (this.classList.contains('active')) this.trapDialogFocus();
      else if (!options.keepClosed) this.open();
      window.HMLpBonusUI?.render();
    });
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector).innerHTML;
  }

  // Noom T05 (13.09): keep the old header's icon markup, replace/insert/remove only its .cart-count-bubble
  syncCountBubble(link, html) {
    if (typeof html !== 'string') return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-count-bubble');
    const current = link.querySelector('.cart-count-bubble');
    if (fresh) {
      const node = document.importNode(fresh, true);
      if (current) {
        // the old header styles its bubble inline (colours from theme settings): keep that style attribute
        if (current.getAttribute('style')) node.setAttribute('style', current.getAttribute('style'));
        current.replaceWith(node);
      } else {
        link.appendChild(node);
      }
    } else if (current) {
      current.remove();
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'hm-cart-drawer',
        selector: '#CartDrawer',
      },
      {
        // Noom port (12.09): `id` is the Liquid section to fetch, `target` the element that receives its inner HTML
        // (the section file renders only the bubble's contents; the wrapper #cart-icon-bubble lives in elora-header)
        id: 'hm-cart-icon-bubble',
        target: '#cart-icon-bubble',
      },
    ];
    // Noom R1 (14.09): an add from the drawer's own recommendations while the cart PAGE is open (global cart) re-renders the
    // page's list and footer from the same /cart/add response — `id` is the page's section id, `selector` the element on both sides
    const mainItems = document.getElementById('main-cart-items');
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainItems && mainItems.dataset.id) sections.push({ id: mainItems.dataset.id, selector: '#main-cart-items .js-contents' });
    if (mainFooter && mainFooter.dataset.id) sections.push({ id: mainFooter.dataset.id, selector: '#main-cart-footer .js-contents' });
    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    const cache = this.sectionDocumentCache || (this.sectionDocumentCache = []);
    const index = cache.findIndex((entry) => entry.html === html);
    let document;
    if (index >= 0) {
      const entry = cache.splice(index, 1)[0];
      cache.push(entry);
      document = entry.document;
    } else {
      document = new DOMParser().parseFromString(html, 'text/html');
      cache.push({ html, document });
      if (cache.length > 5) cache.shift();
    }
    return document.querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'hm-cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'hm-cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
    // Noom R1 (14.09): a change in the drawer while the cart PAGE is open also re-renders the page's list and footer from the
    // same /cart/change response (one write; both views agree; review P1 — both directions)
    const mainItems = document.getElementById('main-cart-items');
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainItems && mainItems.dataset.id) sections.push({ id: 'main-cart-items', section: mainItems.dataset.id, selector: '.js-contents' });
    if (mainFooter && mainFooter.dataset.id) sections.push({ id: 'main-cart-footer', section: mainFooter.dataset.id, selector: '.js-contents' });
    return sections;
  }

  // Dawn 16.0.0 looks for #Quantity-N here, but the drawer's inputs are #Drawer-quantity-N (see snippets/cart-drawer.liquid),
  // so an invalid quantity (0, below the minimum) threw and left the wrong number in the field. Reset the drawer's own input.
  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`) || this.querySelector(`#Quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
    // the field shows a valid number again: drop Dawn's custom message as soon as the shopper moves on
    const clear = () => {
      if (input.value === input.getAttribute('value')) input.setCustomValidity('');
    };
    input.addEventListener('focusout', clear, { once: true });
    input.addEventListener('input', clear, { once: true });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
